import React, { useState, useRef, useEffect } from "react";
import slugify from "../../_helpers/slugify";
import supabase from "../../lib/supabaseClient";
import { toast } from "react-toastify";

const Spinner = () => <div className="admin-spinner" />;

// ─── Image Preview Item ──────────────────────────────────
const ImagePreviewItem = ({ image, index, onRemove, onReplace }) => {
  const replaceInputRef = useRef(null);

  const handleReplaceClick = () => {
    replaceInputRef.current?.click();
  };

  const handleReplaceFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      onReplace(index, file);
    }
    e.target.value = "";
  };

  return (
    <div className="image-preview-item">
      <div className="image-preview-container">
        <img src={image.preview} alt={`preview-${index}`} />
      </div>
      <div className="image-preview-actions">
        <button
          type="button"
          className="btn-img-action btn-img-replace"
          onClick={handleReplaceClick}
          title="جایگزینی"
        >
          🔄
        </button>
        <button
          type="button"
          className="btn-img-action btn-img-delete"
          onClick={() => onRemove(index)}
          title="حذف"
        >
          🗑️
        </button>
      </div>
      <span className="image-index">
        {index + 1}
        {image.isExisting && <span className="existing-badge">موجود</span>}
      </span>
      <input
        type="file"
        accept="image/*"
        ref={replaceInputRef}
        onChange={handleReplaceFile}
        style={{ display: "none" }}
      />
    </div>
  );
};

// ─── Project Form ────────────────────────────────────────
const emptyForm = {
  title_fa: "",
  title_en: "",
  description_fa: "",
  description_en: "",
  area_fa: "",
  area_en: "",
  location_fa: "",
  location_en: "",
  year_fa: "",
  year_en: "",
  is_published: false,
};

export const ProjectForm = ({ initial, onSubmit, onCancel, loading }) => {
  const [form, setForm] = useState(emptyForm);
  const [images, setImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const fileInputRef = useRef(null);

  // بارگذاری داده‌های اولیه برای ویرایش
  useEffect(() => {
    if (initial) {
      setForm({
        title_fa: initial.title_fa || "",
        title_en: initial.title_en || "",
        description_fa: initial.description_fa || "",
        description_en: initial.description_en || "",
        area_fa: initial.area_fa || "",
        area_en: initial.area_en || "",
        location_fa: initial.location_fa || "",
        location_en: initial.location_en || "",
        year_fa: initial.year_fa || "",
        year_en: initial.year_en || "",
        is_published: initial.is_published || false,
      });

      // بارگذاری عکس‌های موجود
      if (initial.image_urls && initial.image_urls.length > 0) {
        const existingImages = initial.image_urls.map((url) => ({
          preview: url,
          url: url,
          isExisting: true,
        }));
        setImages(existingImages);
      }
    }
  }, [initial]);

  // اعتبارسنجی فیلدها
  const validateField = (name, value) => {
    const newErrors = { ...errors };

    if (name === "title_fa" && !value.trim()) {
      newErrors.title_fa = "عنوان فارسی الزامی است";
    } else if (name === "title_fa") {
      delete newErrors.title_fa;
    }

    if (name === "title_en" && !value.trim()) {
      newErrors.title_en = "عنوان انگلیسی الزامی است";
    } else if (name === "title_en") {
      delete newErrors.title_en;
    }

    setErrors(newErrors);
  };

  // تغییر مقادیر فرم
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setForm((prev) => ({ ...prev, [name]: newValue }));
    validateField(name, newValue);
  };

  // blur برای نمایش خطا
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  // فشرده‌سازی عکس
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          // محدود کردن سایز به 1920px
          const maxSize = 1920;
          if (width > height && width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              resolve(
                new File([blob], file.name, {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                }),
              );
            },
            "image/jpeg",
            0.85, // کیفیت 85%
          );
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // انتخاب عکس‌های جدید
  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newImages = [];
    for (const file of files) {
      const compressed = await compressImage(file);
      newImages.push({
        file: compressed,
        preview: URL.createObjectURL(compressed),
        isExisting: false,
      });
    }

    setImages((prev) => [...prev, ...newImages]);
    e.target.value = "";
  };

  // حذف عکس
  const handleRemoveImage = (index) => {
    setImages((prev) => {
      const updated = [...prev];

      if (updated[index].preview && !updated[index].isExisting) {
        URL.revokeObjectURL(updated[index].preview);
      }

      updated.splice(index, 1);
      return updated;
    });
  };

  // جایگزینی عکس
  const handleReplaceImage = async (index, file) => {
    const compressed = await compressImage(file);

    setImages((prev) => {
      const updated = [...prev];

      if (updated[index].preview && !updated[index].isExisting) {
        URL.revokeObjectURL(updated[index].preview);
      }

      updated[index] = {
        file: compressed,
        preview: URL.createObjectURL(compressed),
        isExisting: false,
      };

      return updated;
    });
  };

  // ارسال فرم
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = {};

    if (!form.title_fa.trim()) {
      validationErrors.title_fa = "عنوان فارسی الزامی است";
    }

    if (!form.title_en.trim()) {
      validationErrors.title_en = "عنوان انگلیسی الزامی است";
    }

    if (images.length === 0) {
      validationErrors.images = "حداقل یک تصویر لازم است";
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      await onSubmit({
        ...form,
        images,
        slug: slugify(form.title_en),
      });
    } catch (err) {
      console.error(err);
      toast.error("خطا در ذخیره پروژه");
    }
  };

  return (
    <form className="admin-form project-form" onSubmit={handleSubmit}>
      <div className="form-grid">
        <div className="form-group">
          <label>عنوان فارسی</label>
          <input
            name="title_fa"
            value={form.title_fa}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {touched.title_fa && errors.title_fa && (
            <span className="error">{errors.title_fa}</span>
          )}
        </div>

        <div className="form-group">
          <label>Title (EN)</label>
          <input
            name="title_en"
            value={form.title_en}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {touched.title_en && errors.title_en && (
            <span className="error">{errors.title_en}</span>
          )}
        </div>

        <div className="form-group">
          <label>منطقه</label>
          <input name="area_fa" value={form.area_fa} onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>Area (EN)</label>
          <input name="area_en" value={form.area_en} onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>موقعیت</label>
          <input
            name="location_fa"
            value={form.location_fa}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Location (EN)</label>
          <input
            name="location_en"
            value={form.location_en}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>سال ساخت</label>
          <input name="year_fa" value={form.year_fa} onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>Year</label>
          <input name="year_en" value={form.year_en} onChange={handleChange} />
        </div>
      </div>

      <div className="form-group full">
        <label>توضیحات فارسی</label>
        <textarea
          name="description_fa"
          rows={4}
          value={form.description_fa}
          onChange={handleChange}
        />
      </div>

      <div className="form-group full">
        <label>Description (EN)</label>
        <textarea
          name="description_en"
          rows={4}
          value={form.description_en}
          onChange={handleChange}
        />
      </div>

      <div className="form-group full">
        <label>تصاویر پروژه</label>

        <div className="image-preview-grid">
          {images.map((img, index) => (
            <ImagePreviewItem
              key={index}
              image={img}
              index={index}
              onRemove={handleRemoveImage}
              onReplace={handleReplaceImage}
            />
          ))}
        </div>

        {errors.images && <span className="error">{errors.images}</span>}

        <button
          type="button"
          className="btn-upload"
          onClick={() => fileInputRef.current.click()}
        >
          افزودن تصویر
        </button>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleImageSelect}
          style={{ display: "none" }}
        />
      </div>

      <div className="form-group checkbox">
        <label>
          <input
            type="checkbox"
            name="is_published"
            checked={form.is_published}
            onChange={handleChange}
          />
          انتشار پروژه
        </label>
      </div>

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          انصراف
        </button>

        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? "در حال ذخیره..." : "ذخیره پروژه"}
        </button>
      </div>
    </form>
  );
};
